import './MonitorApplication.scss';

import { AppDisplay } from '@ui/applications';
import { Link } from 'react-router-dom';
import { monitorMenuConfigs } from './MonitorMenu';
import { SonarText } from '@ui/text';
import { useDataSecurityMonitors } from '@hooks/data-security/useDataSecurityMonitors';

import { Alert, Button, Form, Select, Space } from 'antd';
import { COPY_SPECIFIC, FULL_ROUTES, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import {
	DataSecurityApplication,
	DataSecurityMonitor,
	DataSecurityMonitorType,
	MonitorFormTypes
} from '@sonar-software/sonar-core';
import { Else, If, Then, When } from 'react-if';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDataSecurityApps, usePrevious } from '@hooks';

export interface MonitorApplicationProps {
	editing?: boolean,
	fieldId?: number,
	hidden?: boolean,
	overrideMonitors?: Array<MonitorFormTypes>,
	monitor: MonitorFormTypes,
	onChange: (value?: number) => void,
}

export function getApplicationList(
	monitors: Array<Partial<DataSecurityMonitor>>,
	applications: Array<DataSecurityApplication>,
	type?: DataSecurityMonitorType
) {
	if (type === undefined) {
		return undefined;
	}

	const monitorsByType = monitors.filter((monitor) => monitor.monitorConfig?.monitorTypeId === type);
	const maxCount = monitorMenuConfigs.find((config) => config.type === type)?.max ?? 1;

	return applications.reduce((result, application) => (monitorsByType
		.filter((monitor) => monitor.applicationId === application.applicationId)
		.length >= maxCount ?
		[ ...result ] :
		[ ...result, {
			label: application.name,
			value: application.applicationId
		} ]), []);
}

export const MonitorApplication: React.FC<MonitorApplicationProps> = ({
	editing,
	fieldId,
	hidden = false,
	monitor,
	onChange
}) => {
	const monitors = useDataSecurityMonitors();
	const applications = useDataSecurityApps();
	const previousMonitor = usePrevious(monitor);

	const applicationOptions = useMemo(() =>
		getApplicationList(monitors, applications, monitor.monitorConfig?.monitorTypeId),
	[ monitors, applications, monitor ]);

	const defaultApp = useMemo(() => {
		if (monitor.applicationId) {
			return monitor.applicationId;
		}

		const appOptions = getApplicationList(monitors, applications, monitor.monitorConfig?.monitorTypeId);

		if (previousMonitor?.applicationId && appOptions?.find((x) => x.value === previousMonitor.applicationId)) {
			return previousMonitor.applicationId;
		} else if (appOptions?.length === 1) {
			return appOptions[ 0 ].value;
		}

		return undefined;
	}, [ monitor, applications, monitors, previousMonitor ]);

	const handleOnChange = useCallback((newApp: number | undefined) => {
		if (onChange) {
			onChange(newApp);
		}
	}, [ onChange ]);

	useEffect(() => {
		if (
			(monitor.monitorConfig?.monitorTypeId !== previousMonitor?.monitorConfig?.monitorTypeId ||
			monitor.applicationId !== defaultApp) &&
			!monitor.templateId
		) {
			handleOnChange(defaultApp);
		}
	}, [ defaultApp, previousMonitor, handleOnChange, monitor ]);

	if (!monitor.monitorConfig?.monitorTypeId) {
		return;
	}

	return (
		<If condition={ monitor.templateId }>
			<Then>
				<SonarText className='h3'>
					{ COPY_SPECIFIC.IMPACTED_APPLICATIONS }
				</SonarText>
				<Form.Item
					className='monitor-application'
					name={ fieldId === undefined ? 'applicationIds' : [ fieldId, 'applicationIds' ] }>
					<Space>
						<If condition={ monitor.applicationIds && monitor.applicationIds?.length > 0 }>
							<Then>
								{ monitor.applicationIds?.map((appId) =>
									<AppDisplay
										appId={ appId }
										key={ appId } />) }
							</Then>
							<Else>
								<Alert
									message={
										<Space>
											{ COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS_MESSAGE }
											<Link to={ FULL_ROUTES.DATA_SECURITY_APPLICATION_SETUP }>
												<Button
													size='small'
													type='primary'>{ COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS }
												</Button>
											</Link>
										</Space>
									}
									showIcon
									type='info' />
							</Else>
						</If>
					</Space>
				</Form.Item>
			</Then>
			<Else>
				<When condition={ !hidden }>
					<SonarText className='h3'>
						{ COPY_SPECIFIC.SELECT_AN_APP }
					</SonarText>
				</When>
				<Form.Item
					className='monitor-application'
					hidden={ hidden }
					initialValue={ defaultApp }
					name={ fieldId === undefined ? 'applicationId' : [ fieldId, 'applicationId' ] }>
					<Select
						disabled={ !editing }
						onChange={ handleOnChange }
						optionLabelProp='label'
						optionRender={ (app) => <AppDisplay appId={ Number(app.value) } /> }
						options={ applicationOptions }
						placeholder={ PLACEHOLDERS_COPY.SELECT_AN_APPLICATION }
						showSearch />
				</Form.Item>
			</Else>
		</If>
	);
};
